import React, { useEffect, useState } from 'react';
import { Appointment, SageSyncJobStatusResponse } from '@/types';
import { X } from 'lucide-react';
import { showAlertModal } from '@shared/components/common/AlertModal';
import { api } from '@/services/api';
import { normalizeDateOnly } from '@/lib/dates';
import { normalizeErrorMessage } from '@/lib/errorUtils';

interface SageSelectionGroup {
  key: string;
  companyId: string;
  companyName: string;
  month: string;
  count: number;
  estimatedTotal: number;
}

function buildSelectionGroups(
  appointments: Appointment[],
  companiesById: Map<string, string>
): SageSelectionGroup[] {
  const groups = new Map<string, SageSelectionGroup>();
  appointments.forEach((app) => {
    const month = normalizeDateOnly(app.date)?.slice(0, 7) || 'unknown';
    const companyId = app.companyId || 'unknown';
    const companyName = app.companyName || companiesById.get(companyId) || 'Unknown company';
    const key = `${companyId}::${month}`;
    const existing = groups.get(key) || {
      key,
      companyId,
      companyName,
      month,
      count: 0,
      estimatedTotal: 0
    };
    existing.count += 1;
    existing.estimatedTotal += Number(app.subtotalAmount || 0);
    groups.set(key, existing);
  });
  return [...groups.values()];
}

// --- Sage Confirm Dialog ---

interface SageConfirmDialogProps {
  selectedCount: number;
  selectionGroups: SageSelectionGroup[];
  onConfirm: () => Promise<void>;
  onClose: () => void;
}

export const SageConfirmDialog: React.FC<SageConfirmDialogProps> = ({
  selectedCount,
  selectionGroups,
  onConfirm,
  onClose
}) => {
  const [isQueuing, setIsQueuing] = useState(false);

  const handleConfirm = async () => {
    setIsQueuing(true);
    try {
      await onConfirm();
    } finally {
      setIsQueuing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[110] p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-2xl w-full border border-slate-100">
        <h3 className="font-black text-lg text-slate-900 uppercase tracking-tight mb-2">
          Confirm Sage Draft Sync
        </h3>
        <p className="text-sm text-slate-600 mb-4">
          {selectedCount} booking(s) will be queued and split by company + month.
        </p>
        <div className="max-h-64 overflow-y-auto border rounded-xl">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left p-2">Company</th>
                <th className="text-left p-2">Month</th>
                <th className="text-right p-2">Bookings</th>
                <th className="text-right p-2">Est. Net</th>
              </tr>
            </thead>
            <tbody>
              {selectionGroups.map((group) => (
                <tr key={group.key} className="border-t">
                  <td className="p-2">{group.companyName}</td>
                  <td className="p-2">{group.month}</td>
                  <td className="p-2 text-right">{group.count}</td>
                  <td className="p-2 text-right">£{group.estimatedTotal.toFixed(2)}</td>
                </tr>
              ))}
              {selectionGroups.length === 0 && (
                <tr>
                  <td className="p-3 text-slate-500 text-center" colSpan={4}>
                    No selected bookings.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button
            className="px-4 py-2 rounded-xl text-slate-600 hover:bg-slate-100"
            onClick={onClose}
            disabled={isQueuing}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 rounded-xl bg-teal-600 text-white font-bold hover:bg-teal-700 disabled:opacity-60"
            onClick={handleConfirm}
            disabled={isQueuing || selectedCount === 0}
          >
            {isQueuing ? 'Queueing...' : 'Queue Sage Sync'}
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Sage Job Panel ---

interface SageJobPanelProps {
  jobId: string;
  onClose: () => void;
  onJobComplete?: () => void;
}

function isSageTerminalStatus(status: string | null | undefined): boolean {
  return status === 'completed' || status === 'failed' || status === 'partial';
}

export const SageJobPanel: React.FC<SageJobPanelProps> = ({ jobId, onClose, onJobComplete }) => {
  const [jobStatus, setJobStatus] = useState<SageSyncJobStatusResponse | null>(null);

  useEffect(() => {
    let isCurrent = true;
    let intervalId: number | null = null;

    const poll = async () => {
      try {
        const status = await api.getStaffSageSyncJobStatus(jobId);
        if (!isCurrent) return;
        setJobStatus(status);
        if (isSageTerminalStatus(status?.job?.status)) {
          if (intervalId != null) {
            window.clearInterval(intervalId);
            intervalId = null;
          }
          onJobComplete?.();
        }
      } catch {
        if (!isCurrent) return;
      }
    };

    poll();
    intervalId = window.setInterval(poll, 2500);
    return () => {
      isCurrent = false;
      if (intervalId != null) window.clearInterval(intervalId);
    };
  }, [jobId, onJobComplete]);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[110] p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-3xl w-full border border-slate-100 max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h3 className="font-black text-lg text-slate-900 uppercase tracking-tight">
              Sage Sync Job
            </h3>
            <p className="text-xs text-slate-500 font-mono">
              {jobStatus?.job?.id || jobId || 'Pending'}
            </p>
          </div>
          <button className="text-slate-400 hover:text-slate-600" onClick={onClose}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          {[
            { label: 'Status', value: jobStatus?.job?.status || 'Queued', bg: 'bg-slate-50' },
            {
              label: 'Processed',
              value: `${jobStatus?.job?.processedItems || 0}/${jobStatus?.job?.totalItems || 0}`,
              bg: 'bg-slate-50'
            },
            {
              label: 'Synced',
              value: jobStatus?.job?.succeededItems || 0,
              bg: 'bg-green-50',
              textColor: 'text-green-500',
              valColor: 'text-green-700'
            },
            {
              label: 'Skipped',
              value: jobStatus?.job?.skippedItems || 0,
              bg: 'bg-amber-50',
              textColor: 'text-amber-500',
              valColor: 'text-amber-700'
            },
            {
              label: 'Failed',
              value: jobStatus?.job?.failedItems || 0,
              bg: 'bg-red-50',
              textColor: 'text-red-500',
              valColor: 'text-red-700'
            }
          ].map(({ label, value, bg, textColor, valColor }) => (
            <div key={label} className={`${bg} rounded-lg p-2`}>
              <p className={`text-[10px] uppercase font-black ${textColor || 'text-slate-400'}`}>
                {label}
              </p>
              <p className={`text-sm font-bold ${valColor || 'text-slate-800'}`}>{value}</p>
            </div>
          ))}
        </div>

        <div className="border rounded-xl overflow-auto flex-1">
          <table className="w-full text-xs">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left p-2">Booking</th>
                <th className="text-left p-2">Company</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">Invoice</th>
                <th className="text-left p-2">Error</th>
              </tr>
            </thead>
            <tbody>
              {(jobStatus?.items || []).map((item) => (
                <tr key={item.id} className="border-t">
                  <td className="p-2 font-mono">{item.bookingId}</td>
                  <td className="p-2">{item.companyName || '-'}</td>
                  <td className="p-2">{item.status}</td>
                  <td className="p-2">{item.sageInvoiceNumber || '-'}</td>
                  <td className="p-2 text-red-600">{item.errorMessage || '-'}</td>
                </tr>
              ))}
              {(jobStatus?.items || []).length === 0 && (
                <tr>
                  <td className="p-3 text-slate-500 text-center" colSpan={5}>
                    Waiting for job details...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// --- Hook: useSageSync ---

interface UseSageSyncOptions {
  selectedBookingIds: Set<string>;
  selectedAppointments: Appointment[];
  companiesById: Map<string, string>;
  onClearSelection: () => void;
  onForceRefresh?: () => void;
}

export function useSageSync({
  selectedBookingIds,
  selectedAppointments,
  companiesById,
  onClearSelection,
  onForceRefresh
}: UseSageSyncOptions) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobPanelOpen, setJobPanelOpen] = useState(false);

  const selectionGroups = buildSelectionGroups(selectedAppointments, companiesById);

  const handleQueue = async () => {
    if (selectedBookingIds.size === 0) {
      showAlertModal('Select at least one booking to send to Sage.');
      return;
    }
    try {
      const response = await api.queueStaffSageSyncJob([...selectedBookingIds]);
      onClearSelection();
      setConfirmOpen(false);
      setJobId(response.jobId);
      setJobPanelOpen(true);
    } catch (error: unknown) {
      showAlertModal(normalizeErrorMessage(error, 'Failed to queue Sage sync job.'));
    }
  };

  const handleJobComplete = () => {
    onForceRefresh?.();
  };

  return {
    confirmOpen,
    jobId,
    jobPanelOpen,
    selectionGroups,
    openConfirm: () => setConfirmOpen(true),
    closeConfirm: () => setConfirmOpen(false),
    openJobPanel: () => setJobPanelOpen(true),
    closeJobPanel: () => setJobPanelOpen(false),
    handleQueue,
    handleJobComplete
  };
}
